/**
 * Pick one or more actors to filter the log by.
 *
 * A type-to-narrow list rather than a `<select multiple>`: over a busy week
 * the log names more actors than a drop-down can show at once, and the reader
 * usually knows part of the name they are after.
 *
 * The options come from the SERVER, out of the log itself, for the date range
 * currently on screen. That is the only source that can offer the two actors
 * an investigation most often wants and no user directory holds: a background
 * job, and a failed sign-in whose presented identity matched nobody. Those
 * two are marked, because "this name is not an account" is exactly the fact
 * the reader needs to see before believing the row.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, UserRound, X } from 'lucide-react';

import type { AuditActor } from '@/api/client';

import { actorKey, actorLabel, type ActorFilter } from './auditFilters';

/** The filter an option in the list stands for. */
function filterFor(actor: AuditActor): ActorFilter {
  return actor.user_id
    ? { kind: 'user', userId: actor.user_id, label: actor.display }
    : { kind: 'display', display: actor.display };
}

interface Props {
  chosen: readonly ActorFilter[];
  options: readonly AuditActor[];
  loading: boolean;
  onToggle: (actor: ActorFilter) => void;
  onClear: () => void;
}

export function ActorPicker({ chosen, options, loading, onToggle, onClear }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);

  const chosenKeys = useMemo(() => new Set(chosen.map(actorKey)), [chosen]);

  const matches = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((actor) => actor.display.toLowerCase().includes(needle));
  }, [options, search]);

  // Dismiss on an outside click or Escape, like the header's user menu.
  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const summary =
    chosen.length === 0
      ? 'All actors'
      : chosen.length === 1
        ? actorLabel(chosen[0]!)
        : `${chosen.length} actors`;

  return (
    <div className="actor-picker" ref={boxRef}>
      <button
        type="button"
        className={chosen.length > 0 ? 'actor-trigger is-set' : 'actor-trigger'}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <UserRound aria-hidden="true" />
        <span className="actor-trigger-label">{summary}</span>
        <ChevronDown aria-hidden="true" />
      </button>

      {chosen.length > 0 && (
        <button
          type="button"
          className="actor-clear"
          aria-label="Clear the actor filter"
          title="Clear the actor filter"
          onClick={onClear}
        >
          <X aria-hidden="true" />
        </button>
      )}

      {open && (
        <div className="actor-menu">
          <div className="search-field actor-search">
            <Search aria-hidden="true" />
            <input
              type="search"
              value={search}
              autoFocus
              placeholder="Find an actor"
              aria-label="Find an actor"
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          <ul className="actor-options" role="listbox" aria-multiselectable="true">
            {loading && <li className="hint actor-empty">Reading the log for actors.</li>}

            {!loading && matches.length === 0 && (
              <li className="hint actor-empty">
                {options.length === 0
                  ? 'No entries in this date range, so there is nobody to pick.'
                  : 'Nobody in this range matches.'}
              </li>
            )}

            {matches.map((actor) => {
              const filter = filterFor(actor);
              const selected = chosenKeys.has(actorKey(filter));
              return (
                <li key={actorKey(filter)}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={selected ? 'actor-option is-chosen' : 'actor-option'}
                    onClick={() => onToggle(filter)}
                  >
                    <span className="actor-check" aria-hidden="true">
                      {selected && <Check />}
                    </span>
                    <span className="actor-option-name">{actor.display}</span>
                    {!actor.user_id && (
                      <span className="badge pill-neutral actor-kind">no account</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
